import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EducationStep = ({ formData, updateFormData, errors, onNext, onPrevious }) => {
  const [showAddEducation, setShowAddEducation] = useState(false);
  const [newEducation, setNewEducation] = useState({
    degree: '',
    field: '',
    institution: '',
    year: '',
    location: ''
  });

  const degreeTypes = [
    { value: 'high-school', label: 'High School Diploma' },
    { value: 'associate', label: 'Associate Degree' },
    { value: 'bachelor', label: 'Bachelor\'s Degree' },
    { value: 'master', label: 'Master\'s Degree' },
    { value: 'phd', label: 'PhD/Doctorate' },
    { value: 'certificate', label: 'Professional Certificate' },
    { value: 'bootcamp', label: 'Coding Bootcamp' },
    { value: 'other', label: 'Other' }
  ];

  const currentYear = new Date()?.getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => ({
    value: (currentYear - i)?.toString(),
    label: (currentYear - i)?.toString()
  }));

  const languages = [
    { value: 'norwegian', label: 'Norwegian (Norsk)' },
    { value: 'english', label: 'English' },
    { value: 'swedish', label: 'Swedish (Svenska)' },
    { value: 'danish', label: 'Danish (Dansk)' },
    { value: 'german', label: 'German (Deutsch)' },
    { value: 'french', label: 'French (Français)' },
    { value: 'spanish', label: 'Spanish (Español)' },
    { value: 'other', label: 'Other' }
  ];

  const proficiencyLevels = [
    { value: 'native', label: 'Native' },
    { value: 'fluent', label: 'Fluent' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'basic', label: 'Basic' }
  ];

  const addEducation = () => {
    if (newEducation?.degree && newEducation?.institution) {
      const education = {
        id: Date.now(),
        ...newEducation
      };
      updateFormData('education', [...formData?.education, education]);
      setNewEducation({
        degree: '',
        field: '',
        institution: '',
        year: '',
        location: ''
      });
      setShowAddEducation(false);
    }
  };

  const removeEducation = (educationId) => {
    const updatedEducation = formData?.education?.filter(edu => edu?.id !== educationId);
    updateFormData('education', updatedEducation);
  };

  const addLanguage = () => {
    const newLang = {
      id: Date.now(),
      language: 'english',
      proficiency: 'intermediate'
    };
    updateFormData('languages', [...formData?.languages, newLang]);
  };

  const updateLanguage = (langId, field, value) => {
    const updatedLanguages = formData?.languages?.map(lang =>
      lang?.id === langId ? { ...lang, [field]: value } : lang
    );
    updateFormData('languages', updatedLanguages);
  };

  const removeLanguage = (langId) => {
    const updatedLanguages = formData?.languages?.filter(lang => lang?.id !== langId);
    updateFormData('languages', updatedLanguages);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Education & Languages
        </h2>
        <p className="text-muted-foreground">
          Share your educational background and language skills
        </p>
      </div>
      <div className="space-y-8">
        {/* Education Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Education</h3>
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              onClick={() => setShowAddEducation(true)}
            >
              Add Education
            </Button>
          </div>

          {/* Existing Education */}
          {formData?.education?.length > 0 && (
            <div className="space-y-3 mb-4">
              {formData?.education?.map((edu) => (
                <div key={edu?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">
                        {degreeTypes?.find(d => d?.value === edu?.degree)?.label}
                        {edu?.field && ` in ${edu?.field}`}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {edu?.institution}
                        {edu?.location && `, ${edu?.location}`}
                      </p>
                      {edu?.year && (
                        <p className="text-sm text-muted-foreground">
                          Graduated: {edu?.year}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeEducation(edu?.id)}
                      className="p-1 text-muted-foreground hover:text-error transition-colors"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Education Form */}
          {showAddEducation && (
            <div className="bg-card border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-foreground">Add Education</h4>
                <button
                  onClick={() => setShowAddEducation(false)}
                  className="p-1 text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  label="Degree Type"
                  options={degreeTypes}
                  value={newEducation?.degree}
                  onChange={(value) => setNewEducation(prev => ({ ...prev, degree: value }))}
                  required
                />

                <Input
                  label="Field of Study"
                  type="text"
                  placeholder="e.g., Computer Science"
                  value={newEducation?.field}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, field: e?.target?.value }))}
                />
              </div>

              <Input
                label="Institution"
                type="text"
                placeholder="e.g., University of Oslo"
                value={newEducation?.institution}
                onChange={(e) => setNewEducation(prev => ({ ...prev, institution: e?.target?.value }))}
                required
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  label="Graduation Year"
                  options={years}
                  value={newEducation?.year}
                  onChange={(value) => setNewEducation(prev => ({ ...prev, year: value }))}
                  searchable
                />

                <Input
                  label="Location"
                  type="text"
                  placeholder="e.g., Oslo, Norway"
                  value={newEducation?.location}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, location: e?.target?.value }))}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddEducation(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={addEducation}
                  disabled={!newEducation?.degree || !newEducation?.institution}
                >
                  Add Education
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Languages Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Languages</h3>
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              onClick={addLanguage}
            >
              Add Language
            </Button>
          </div>

          {formData?.languages?.length > 0 ? (
            <div className="space-y-3">
              {formData?.languages?.map((lang) => (
                <div key={lang?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Select
                      label="Language"
                      options={languages}
                      value={lang?.language}
                      onChange={(value) => updateLanguage(lang?.id, 'language', value)}
                    />

                    <div className="flex items-end space-x-2">
                      <div className="flex-1">
                        <Select
                          label="Proficiency"
                          options={proficiencyLevels}
                          value={lang?.proficiency}
                          onChange={(value) => updateLanguage(lang?.id, 'proficiency', value)}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => removeLanguage(lang?.id)}
                        className="text-error hover:text-error"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Languages" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No languages added yet</p>
            </div>
          )}
        </div>

        {/* Certifications */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Certifications & Licenses
          </label>
          <textarea
            placeholder="List any relevant certifications, licenses, or professional qualifications..."
            value={formData?.certifications}
            onChange={(e) => updateFormData('certifications', e?.target?.value)}
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
          />
        </div>
      </div>
      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onPrevious} iconName="ArrowLeft">
          Previous
        </Button>
        <Button
          onClick={onNext}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default EducationStep;